import { KDF_ITERATIONS } from '@circa/shared/config';
import {
  aesDecrypt,
  aesEncrypt,
  exportPrivateKey,
  exportPublicKey,
  fromB64,
  generateRsaKeyPair,
  hkdf,
  importAesKey,
  importPrivateKey,
  pbkdf2,
  randomBytes,
  toB64,
  unwrapRawKey,
  wrapRawKey,
} from '@circa/shared/crypto';
import type {
  AccountKeys,
  DeviceInfo,
  PreloginResponse,
  RegisterRequest,
  SessionResponse,
} from '@circa/shared/protocol';
import { useSyncExternalStore } from 'react';
import { db, deleteMeta, getMeta, requestPersistentStorage, setMeta } from './db';
import { keys } from './keys';
import { clearMemory, flush, loadAll } from './repo';

// Account lifecycle: create (local or on a server), sign in on a new device, unlock, lock.
//
// Key hierarchy:
//   master password ──PBKDF2──► master key ──HKDF──► auth key (server login, hashed there)
//                                          └─HKDF──► key-encryption key ──wraps──► data key
//   data key: random AES-256, encrypts every record, body and file
//   RSA key pair: the Worker seals AI media with the public key; private key encrypted by data key
//   vaults: their own random keys, wrapped by keys derived from vault passwords

export type Phase = 'loading' | 'welcome' | 'locked' | 'ready';

export interface AccountMeta extends AccountKeys {
  /** '' = local only (no sync yet) */
  serverUrl: string;
  userId?: string;
  deviceId?: string;
  createdAt: number;
}

interface PinMeta {
  salt: string;
  iterations: number;
  wrapped: string;
}

interface SessionState {
  phase: Phase;
  account: AccountMeta | null;
  hasPin: boolean;
}

let state: SessionState = { phase: 'loading', account: null, hasPin: false };
let token: string | null = null;
const listeners = new Set<() => void>();
const unlockHooks = new Set<() => void | Promise<void>>();
const lockHooks = new Set<() => void | Promise<void>>();

function set(patch: Partial<SessionState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export function useSession(): SessionState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}

export const getSession = () => state;
export const getToken = () => token;

/** '' on the web (same origin as the Worker) unless a server URL was set. */
export function getServerUrl(): string {
  return (state.account?.serverUrl ?? '').replace(/\/+$/, '');
}

export function hasServer(): boolean {
  return !!state.account?.userId && !!token;
}

/** Runs after every unlock (sync start, reminders…). */
export function onUnlocked(fn: () => void | Promise<void>): () => void {
  unlockHooks.add(fn);
  return () => unlockHooks.delete(fn);
}

export function onLocked(fn: () => void | Promise<void>): () => void {
  lockHooks.add(fn);
  return () => lockHooks.delete(fn);
}

// ------------------------------------------------------------------ helpers

async function deriveAccountKeys(password: string, saltB64: string, iterations: number) {
  const master = await pbkdf2(password, fromB64(saltB64), iterations);
  const authKey = toB64(await hkdf(master, 'ember-auth'));
  const kek = await importAesKey(await hkdf(master, 'ember-kek'));
  return { authKey, kek };
}

function defaultServerUrl(): string {
  // The web build is served by the Worker itself, so its API is same-origin.
  return __NATIVE__ ? '' : '';
}

export function deviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const android = /Android/i.test(ua);
  const windows = /Windows/i.test(ua);
  const tauri = '__TAURI_INTERNALS__' in window;
  const platform = android ? 'android' : tauri && windows ? 'windows' : 'web';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const name = android
    ? 'Android phone'
    : tauri
      ? 'Windows app'
      : `${browser} on ${windows ? 'Windows' : /Mac/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'web'}`;
  return { name, platform };
}

async function openWithRawDataKey(raw: Uint8Array, account: AccountMeta, remember: boolean) {
  const dk = await importAesKey(raw, false);
  keys.setDataKey(dk);
  const pkcs8 = await aesDecrypt(dk, fromB64(account.encPrivateKey));
  keys.setPrivateKey(await importPrivateKey(pkcs8));
  if (remember) await setMeta('rememberedKey', dk);
  else await deleteMeta('rememberedKey');
  await finishUnlock();
}

async function finishUnlock() {
  token = (await getMeta<string>('token')) ?? null;
  await loadAll();
  set({ phase: 'ready', hasPin: !!(await getMeta<PinMeta>('pin')) });
  void requestPersistentStorage();
  for (const fn of unlockHooks) void fn();
}

// ------------------------------------------------------------------ boot / unlock

export async function boot(): Promise<void> {
  const account = (await getMeta<AccountMeta>('account')) ?? null;
  const hasPin = !!(await getMeta<PinMeta>('pin'));
  if (!account) {
    set({ phase: 'welcome', account: null, hasPin: false });
    return;
  }
  set({ account, hasPin });
  const remembered = await getMeta<CryptoKey>('rememberedKey');
  if (remembered) {
    try {
      keys.setDataKey(remembered);
      const pkcs8 = await aesDecrypt(remembered, fromB64(account.encPrivateKey));
      keys.setPrivateKey(await importPrivateKey(pkcs8));
      await finishUnlock();
      return;
    } catch {
      keys.clear();
      await deleteMeta('rememberedKey');
    }
  }
  set({ phase: 'locked' });
}

export async function unlock(password: string, remember: boolean): Promise<void> {
  const account = state.account;
  if (!account) throw new Error('No account on this device');
  const { kek } = await deriveAccountKeys(password, account.salt, account.iterations);
  let raw: Uint8Array;
  try {
    raw = await unwrapRawKey(account.wrappedDataKey, kek);
  } catch {
    throw new Error('Wrong password');
  }
  await openWithRawDataKey(raw, account, remember);
}

export async function unlockWithPin(pin: string): Promise<void> {
  const account = state.account;
  const meta = await getMeta<PinMeta>('pin');
  if (!account || !meta) throw new Error('No PIN set');
  const kek = await importAesKey(await pbkdf2(pin, fromB64(meta.salt), meta.iterations));
  let raw: Uint8Array;
  try {
    raw = await unwrapRawKey(meta.wrapped, kek);
  } catch {
    throw new Error('Wrong PIN');
  }
  await openWithRawDataKey(raw, account, false);
}

/** A PIN wraps the data key on this device only. Needs the password to get the raw key. */
export async function setPin(password: string, pin: string | null): Promise<void> {
  const account = state.account;
  if (!account) return;
  if (pin === null) {
    await deleteMeta('pin');
    set({ hasPin: false });
    return;
  }
  const { kek } = await deriveAccountKeys(password, account.salt, account.iterations);
  const raw = await unwrapRawKey(account.wrappedDataKey, kek);
  const salt = randomBytes(16);
  const iterations = 300_000;
  const pinKek = await importAesKey(await pbkdf2(pin, salt, iterations));
  await setMeta('pin', { salt: toB64(salt), iterations, wrapped: await wrapRawKey(raw, pinKek) });
  set({ hasPin: true });
}

export async function lock(): Promise<void> {
  await flush();
  for (const fn of lockHooks) await fn();
  await deleteMeta('rememberedKey');
  keys.clear();
  clearMemory();
  set({ phase: 'locked' });
}

export async function isRemembered(): Promise<boolean> {
  return !!(await getMeta('rememberedKey'));
}

export async function setRemembered(on: boolean): Promise<void> {
  if (on) await setMeta('rememberedKey', keys.dataKey());
  else await deleteMeta('rememberedKey');
}

// ------------------------------------------------------------------ create / sign in

export interface CreateAccountInput {
  email: string;
  password: string;
  /** '' = don't sync yet; same-origin '' on web means "this server" when connect=true */
  serverUrl?: string;
  connect: boolean;
  setupToken?: string;
  remember: boolean;
}

export async function createAccount(input: CreateAccountInput): Promise<void> {
  const salt = randomBytes(16);
  const iterations = KDF_ITERATIONS;
  const { authKey, kek } = await deriveAccountKeys(input.password, toB64(salt), iterations);
  const rawDk = randomBytes(32);
  const dk = await importAesKey(rawDk, false);
  const pair = await generateRsaKeyPair();
  const keysOut: AccountKeys = {
    email: input.email.trim().toLowerCase(),
    salt: toB64(salt),
    iterations,
    wrappedDataKey: await wrapRawKey(rawDk, kek),
    publicKey: await exportPublicKey(pair.publicKey),
    encPrivateKey: toB64(await aesEncrypt(dk, await exportPrivateKey(pair.privateKey))),
  };
  let account: AccountMeta = {
    ...keysOut,
    serverUrl: (input.serverUrl ?? defaultServerUrl()).replace(/\/+$/, ''),
    createdAt: Date.now(),
  };
  if (input.connect) {
    set({ account });
    const res = await registerOnServer(keysOut, authKey, input.setupToken);
    account = { ...account, userId: res.userId, deviceId: res.deviceId };
    await setMeta('token', res.token);
  }
  await setMeta('account', account);
  set({ account });
  await openWithRawDataKey(rawDk, account, input.remember);
}

async function registerOnServer(keysOut: AccountKeys, authKey: string, setupToken?: string) {
  const { api } = await import('./api');
  const body: RegisterRequest = { ...keysOut, authKey, setupToken, device: deviceInfo() };
  return api<SessionResponse>('/api/auth/register', { body, auth: false });
}

/** Connect a local-only account to a server later (Settings → Sync). */
export async function connectServer(
  password: string,
  serverUrl: string,
  setupToken?: string,
): Promise<void> {
  const account = state.account;
  if (!account) throw new Error('No account');
  const { authKey, kek } = await deriveAccountKeys(password, account.salt, account.iterations);
  await unwrapRawKey(account.wrappedDataKey, kek).catch(() => {
    throw new Error('Wrong password');
  });
  const next: AccountMeta = { ...account, serverUrl: serverUrl.replace(/\/+$/, '') };
  set({ account: next });
  const { email, salt, iterations, wrappedDataKey, publicKey, encPrivateKey } = account;
  const res = await registerOnServer(
    { email, salt, iterations, wrappedDataKey, publicKey, encPrivateKey },
    authKey,
    setupToken,
  );
  const saved: AccountMeta = { ...next, userId: res.userId, deviceId: res.deviceId };
  token = res.token;
  await setMeta('token', res.token);
  await setMeta('account', saved);
  // Everything already here needs to go up.
  await db.records.toCollection().modify({ dirty: 1 });
  await db.bodies.toCollection().modify({ dirty: 1 });
  await db.files.toCollection().modify({ uploaded: 0 });
  set({ account: saved });
  for (const fn of unlockHooks) void fn();
}

export interface SignInInput {
  serverUrl: string;
  email: string;
  password: string;
  remember: boolean;
}

export async function signIn(input: SignInInput): Promise<void> {
  const { api } = await import('./api');
  const serverUrl = input.serverUrl.replace(/\/+$/, '');
  const email = input.email.trim().toLowerCase();
  set({
    account: {
      email,
      serverUrl,
      salt: '',
      iterations: 0,
      wrappedDataKey: '',
      publicKey: '',
      encPrivateKey: '',
      createdAt: Date.now(),
    },
  });
  const pre = await api<PreloginResponse>(`/api/auth/prelogin?email=${encodeURIComponent(email)}`, {
    auth: false,
  });
  const { authKey, kek } = await deriveAccountKeys(input.password, pre.salt, pre.iterations);
  const res = await api<SessionResponse>('/api/auth/login', {
    body: { email, authKey, device: deviceInfo() },
    auth: false,
  });
  const raw = await unwrapRawKey(res.account.wrappedDataKey, kek).catch(() => {
    throw new Error('Wrong password');
  });
  const account: AccountMeta = {
    ...res.account,
    serverUrl,
    userId: res.userId,
    deviceId: res.deviceId,
    createdAt: Date.now(),
  };
  token = res.token;
  await setMeta('token', res.token);
  await setMeta('account', account);
  set({ account });
  await openWithRawDataKey(raw, account, input.remember);
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const account = state.account;
  if (!account) throw new Error('No account');
  const old = await deriveAccountKeys(oldPassword, account.salt, account.iterations);
  const raw = await unwrapRawKey(account.wrappedDataKey, old.kek).catch(() => {
    throw new Error('Wrong password');
  });
  const salt = toB64(randomBytes(16));
  const iterations = KDF_ITERATIONS;
  const next = await deriveAccountKeys(newPassword, salt, iterations);
  const wrappedDataKey = await wrapRawKey(raw, next.kek);
  if (hasServer()) {
    const { api } = await import('./api');
    await api('/api/auth/password', {
      body: { oldAuthKey: old.authKey, authKey: next.authKey, salt, iterations, wrappedDataKey },
    });
  }
  const saved: AccountMeta = { ...account, salt, iterations, wrappedDataKey };
  await setMeta('account', saved);
  await deleteMeta('pin');
  set({ account: saved, hasPin: false });
}

/** Deletes the account and all its synced data on the server, then wipes this device. */
export async function deleteAccount(password: string): Promise<void> {
  const account = state.account;
  if (!account) throw new Error('No account');
  const keys = await deriveAccountKeys(password, account.salt, account.iterations);
  await unwrapRawKey(account.wrappedDataKey, keys.kek).catch(() => {
    throw new Error('Wrong password');
  });
  if (hasServer()) {
    const { api } = await import('./api');
    await api('/api/auth/delete', { body: { authKey: keys.authKey } });
  }
  await signOut();
}

/** Removes every trace of the account from this device. */
export async function signOut(): Promise<void> {
  try {
    if (hasServer()) {
      const { api } = await import('./api');
      await api(`/api/auth/devices/${state.account?.deviceId}`, { method: 'DELETE' }).catch(
        () => {},
      );
    }
  } finally {
    for (const fn of lockHooks) await fn();
    keys.clear();
    clearMemory();
    token = null;
    await db.delete();
    localStorage.clear();
    location.replace('/');
  }
}
